import { MapPinOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useEffect, useRef } from 'react';
import {
  Box,
  EmptyState,
  Flex,
  IntentionsSpotsCard,
  ScrollArea,
  type IntentionsSpotsSection,
} from '@/components/common';
import {
  DiscoveryGrid,
  DiscoveryHeader,
  LocationOverrideBanner,
  SetIntentionsDialog,
} from '@/components/discovery';
import { NotesRow } from '@/components/notes';
import { INTENTIONS, type IntentionId } from '@/constants/intentions';
import { useCurrentUser } from '@/contexts';
import { useFeatureFlag } from '@/hooks';
import { usePermissionStatus } from '@/hooks/usePermissionStatus';
import { useScrollRestoration } from '@/hooks/useScrollRestoration';
import { useViewerLocation } from '@/hooks/useViewerLocation';
import { useFilterStore } from '@/stores/filterStore';

function DiscoveryContent() {
  const { t } = useTranslation('discover');
  const navigate = useNavigate();
  const { currentUser: profile, settings } = useCurrentUser();
  const { isOverride } = useViewerLocation();
  const permissionDenied = usePermissionStatus().location === 'denied';
  const setShowIntentions = useFilterStore(s => s.setShowIntentions);

  // An admin override browses from a fixed place, so device permission and the
  // location toggle don't gate the grid while it's set.
  const locationOff =
    !isOverride && (permissionDenied || (settings !== null && !settings.locationAllowed));

  if (profile?.takingABreak) {
    return (
      <EmptyState
        title={t('takingABreak.title')}
        description={t('takingABreak.description')}
        action={{
          label: t('takingABreak.action'),
          onClick: () => setShowIntentions(true),
        }}
      />
    );
  }

  if (locationOff) {
    return (
      <EmptyState
        icon={<MapPinOff color="gray" size={48} strokeWidth={1.5} />}
        title={t('locationOff.title')}
        description={t('locationOff.description')}
        action={{
          label: t('locationOff.action'),
          onClick: () => navigate('/account/settings/preferences'),
        }}
      />
    );
  }

  return (
    <>
      {isOverride && (
        <Box mb="3">
          <LocationOverrideBanner />
        </Box>
      )}
      <DiscoveryGrid />
    </>
  );
}

export function DiscoverPage() {
  const { t } = useTranslation('discover');
  const { t: tOnboarding } = useTranslation('onboarding');
  const { currentUser: profile } = useCurrentUser();
  const { enabled: notesRowEnabled } = useFeatureFlag('notes_discover_row');
  const setShowIntentions = useFilterStore(s => s.setShowIntentions);
  const hasNudged = useRef(false);
  const scrollRef = useScrollRestoration<HTMLDivElement>('discover');

  const intentions = (profile?.intentions ?? []).filter((id): id is IntentionId =>
    INTENTIONS.some(option => option.id === id)
  );
  const labels = intentions
    .map(id => INTENTIONS.find(option => option.id === id))
    .filter((option): option is (typeof INTENTIONS)[number] => Boolean(option))
    .map(option => tOnboarding(option.labelKey));

  useEffect(() => {
    if (!profile) return;

    if (profile.takingABreak || intentions.length > 0) {
      setShowIntentions(false);
      hasNudged.current = false;
      return;
    }

    if (hasNudged.current) return;

    hasNudged.current = true;
    setShowIntentions(true);
  }, [profile, intentions.length, setShowIntentions]);

  const sections: IntentionsSpotsSection[] = [
    {
      title: t('iWant.label'),
      labels,
      emptyText: t('iWant.takingBreak'),
      badgeVariant: 'solid',
    },
  ];

  return (
    <Flex direction="column" height="100%">
      <DiscoveryHeader />
      <ScrollArea ref={scrollRef}>
        <Flex direction="column" px="3" pb="2">
          {notesRowEnabled && <NotesRow />}
          <IntentionsSpotsCard
            sections={sections}
            action={
              <SetIntentionsDialog
                initialIntentions={intentions}
                takingABreak={profile?.takingABreak ?? false}
              />
            }
          />
          <DiscoveryContent />
        </Flex>
      </ScrollArea>
    </Flex>
  );
}
